<script setup lang="ts">
  /**
   * An info icon that reveals a short explanation next to whatever it follows —
   * the tooltip the invoice rail gives its icon buttons, as a white card with the
   * text centred, so it reads on both the white sheet and the muted panels.
   *
   * Opens on hover, on focus and on click. The last one matters on touch, where
   * there is no hover, and the first two keep it reachable by pointer and keyboard
   * alike. Escape or a press anywhere outside closes it.
   *
   * The trigger is a real button so it lands in the tab order and announces its
   * state; the panel it names is linked by `aria-controls`.
   *
   * `variant="tooltip"` is the other block on the same card: one dark line of label
   * text beside the element it names, for the collapsed console rail where the icon
   * carries no visible label. It labels the `trigger` slot rather than shipping an
   * info icon, opens on hover and focus only, and describes rather than discloses —
   * `role="tooltip"` + `aria-describedby`, no click pin and no `aria-expanded`.
   */
  import { computed, onBeforeUnmount, ref, useId, watch } from 'vue'

  const props = withDefaults(
    defineProps<{
      /** Names the trigger for assistive tech — say what the panel holds. */
      label?: string
      /**
       * Which side of the trigger the panel opens on. `bottom-right` / `top-right` hang it from the
       * trigger's leading edge and grow it rightward, for a trigger at the left of a clipping box (a
       * table cell); pick `top-right` only where the downward panel would be cut (the last row).
       * Ignored by the tooltip, which only sits right.
       */
      placement?: 'bottom' | 'top' | 'bottom-right' | 'top-right'
      variant?: 'hint' | 'tooltip'
    }>(),
    {
      label: 'More information',
      placement: 'bottom',
      variant: 'hint',
    },
  )

  const slots = defineSlots<{
    default?: () => unknown
    /** The tooltip's labelled element. */
    trigger?: () => unknown
    /** One `BfBadge` that becomes the trigger in place of the info icon (DS 18 `--badge`). */
    badge?: () => unknown
  }>()

  const isTooltip = computed(() => props.variant === 'tooltip')
  const isBadge = computed(() => !isTooltip.value && !!slots.badge)

  const panelId = useId()

  /* Hover and focus peek at the panel, a click pins it open. The two are held
     apart because a mouse click focuses the button first: a single `open` flag
     would be raised by that focus and then dropped again by the click. */
  const isPinned = ref(false)
  const isPeeked = ref(false)

  const isOpen = computed(() => isPinned.value || isPeeked.value)

  const peek = (): void => {
    isPeeked.value = true
  }

  const unpeek = (): void => {
    isPeeked.value = false
  }

  const toggle = (): void => {
    isPinned.value = !isPinned.value
    // A click asking for it to close beats the hover or focus holding it open.
    if (!isPinned.value) unpeek()
  }

  const dismiss = (): void => {
    isPinned.value = false
    unpeek()
  }

  /* A pin outlives the focus that came with the click, so a press elsewhere has to drop it — the
     listener lives only while pinned, not once per hint on the page. */
  const root = ref<HTMLElement | null>(null)

  const onOutsidePointer = (event: PointerEvent): void => {
    if (event.target instanceof Node && !root.value?.contains(event.target)) dismiss()
  }

  watch(isPinned, pinned => {
    if (pinned) document.addEventListener('pointerdown', onOutsidePointer)
    else document.removeEventListener('pointerdown', onOutsidePointer)
  })

  onBeforeUnmount(() => document.removeEventListener('pointerdown', onOutsidePointer))

  const panelClasses = computed(() =>
    isTooltip.value ? 'tooltip__panel tooltip__panel--right' : `hint__panel--${props.placement}`,
  )
</script>

<template>
  <span
    ref="root"
    class="hint"
    :class="{ tooltip: isTooltip }"
    :aria-describedby="isTooltip ? panelId : undefined"
    @mouseenter="peek"
    @mouseleave="unpeek"
    @focusin="peek"
    @focusout="unpeek"
    @keydown.esc="dismiss"
  >
    <slot v-if="isTooltip" name="trigger" />

    <button
      v-else
      type="button"
      class="hint__trigger"
      :class="{ 'hint__trigger--badge': isBadge }"
      :aria-expanded="isOpen"
      :aria-controls="panelId"
      :aria-label="label"
      @click="toggle"
      @focus="peek"
      @blur="unpeek"
      @keydown.esc="dismiss"
    >
      <slot v-if="isBadge" name="badge" />
      <svg
        v-else
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="12" cy="12" r="9.5" />
        <line x1="12" y1="11" x2="12" y2="16.5" />
        <line x1="12" y1="7.75" x2="12" y2="7.75" />
      </svg>
    </button>
    <span
      :id="panelId"
      class="hint__panel"
      :class="panelClasses"
      :role="isTooltip ? 'tooltip' : undefined"
      :data-state="isOpen ? 'open' : undefined"
    >
      <slot />
    </span>
  </span>
</template>

<style scoped lang="scss">
  .hint {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  /* The layout reset is every trigger's; colour and cursor are the bare icon's only, so a badge
     trigger keeps its own tone. */
  .hint__trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: none;
    border: 0;
    transition: color var(--bf-dur-base) var(--bf-ease);
  }

  .hint__trigger:not(.hint__trigger--badge) {
    color: var(--bf-fg-3);
    cursor: pointer;
  }

  /* Same hover accent the invoice rail gives its icon buttons. */
  .hint__trigger:not(.hint__trigger--badge):hover,
  .hint__trigger:not(.hint__trigger--badge):focus-visible {
    color: var(--bf-orange-dark);
  }

  /* The badge is the trigger (DS 18): the badge's tone is untouched; the trigger adds the help
     cursor and the shared focus ring, rounded to the badge's radius. */
  .hint__trigger--badge {
    border-radius: calc(var(--dk-radius) - 4px);
    cursor: help;
  }

  .hint__trigger--badge:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 2px hsl(var(--dk-background)),
      0 0 0 4px hsl(var(--dk-ring));
  }

  /* Centred on the icon so a narrow column cannot push the card past either edge.
     visibility rather than v-if — it keeps the fade and still hides the text from
     assistive tech while the panel is closed. pointer-events, as on the invoice
     rail's tips, so an open card never swallows a click meant for what it covers. */
  .hint__panel {
    position: absolute;
    left: 50%;
    z-index: 2;
    width: max-content;
    max-width: 240px;
    padding: 8px 12px;
    font-family: var(--bf-font-display);
    font-size: 12px;
    font-weight: var(--bf-fw-regular);
    line-height: 1.5;
    color: var(--bf-fg-1);
    text-align: center;
    visibility: hidden;
    background: var(--bf-white);
    border: 1px solid var(--bf-line);
    border-radius: var(--bf-r-md);
    box-shadow: var(--bf-shadow-2);
    opacity: 0;
    pointer-events: none;
    transition:
      opacity 140ms var(--bf-ease),
      transform 140ms var(--bf-ease),
      visibility 140ms;
  }

  /* Two carets stacked: the hairline triangle, then a white one a pixel over it,
     so the point continues the card's border rather than crossing it. */
  .hint__panel::before,
  .hint__panel::after {
    position: absolute;
    left: 50%;
    content: '';
    border: 6px solid transparent;
    transform: translateX(-50%);
  }

  .hint__panel--bottom {
    top: calc(100% + 10px);
    transform: translateX(-50%) translateY(-4px);
  }

  .hint__panel--bottom::before {
    bottom: 100%;
    border-bottom-color: var(--bf-line);
  }

  .hint__panel--bottom::after {
    bottom: calc(100% - 1px);
    border-bottom-color: var(--bf-white);
  }

  .hint__panel--top {
    bottom: calc(100% + 10px);
    transform: translateX(-50%) translateY(4px);
  }

  .hint__panel--top::before {
    top: 100%;
    border-top-color: var(--bf-line);
  }

  .hint__panel--top::after {
    top: calc(100% - 1px);
    border-top-color: var(--bf-white);
  }

  /* Edge-anchored: the panel hangs from the trigger's leading edge and grows rightward, for a
     trigger at the left of a clipping box. The carets sit 14px in, over the trigger. */
  .hint__panel--bottom-right,
  .hint__panel--top-right {
    left: 100%;
    transform: translateX(-8px) translateY(-4px);
  }

  .hint__panel--bottom-right {
    top: calc(100% + 10px);
  }

  .hint__panel--top-right {
    bottom: calc(100% + 10px);
    transform: translateX(-8px) translateY(4px);
  }

  .hint__panel--bottom-right::before,
  .hint__panel--bottom-right::after,
  .hint__panel--top-right::before,
  .hint__panel--top-right::after {
    left: 14px;
    transform: none;
  }

  .hint__panel--bottom-right::before {
    bottom: 100%;
    border-bottom-color: var(--bf-line);
  }

  .hint__panel--bottom-right::after {
    bottom: calc(100% - 1px);
    border-bottom-color: var(--bf-white);
  }

  .hint__panel--top-right::before {
    top: 100%;
    border-top-color: var(--bf-line);
  }

  .hint__panel--top-right::after {
    top: calc(100% - 1px);
    border-top-color: var(--bf-white);
  }

  .hint__panel--bottom-right[data-state='open'],
  .hint__panel--top-right[data-state='open'] {
    transform: translateX(-8px) translateY(0);
  }

  .hint__panel[data-state='open'] {
    visibility: visible;
    opacity: 1;
  }

  .hint__panel--bottom[data-state='open'],
  .hint__panel--top[data-state='open'] {
    transform: translateX(-50%) translateY(0);
  }

  /* On a dark surface the trigger cannot stay --bf-fg-3. */
  .tooltip .hint__trigger {
    color: inherit;
  }

  .tooltip__panel {
    width: auto;
    max-width: none;
    padding: 5px 10px;
    font-size: var(--bf-fs-xs_5);
    text-align: left;
    white-space: nowrap;
    color: var(--bf-fg-inverse-1);
    background: var(--bf-deep-blue);
    border: 0;
    border-radius: var(--bf-r-sm);
    box-shadow: none;
  }

  /* One triangle only: there is no border for a second one to continue. */
  .tooltip__panel::after {
    content: none;
  }

  .tooltip__panel--right {
    top: 50%;
    left: calc(100% + 12px);
    transform: translateY(-50%) translateX(-4px);
  }

  .tooltip__panel--right::before {
    top: 50%;
    left: -4px;
    border-right-color: var(--bf-deep-blue);
    border-left: 0;
    transform: translateY(-50%);
  }

  .tooltip__panel--right[data-state='open'] {
    transform: translateY(-50%);
  }
</style>
